import React, { createContext, useContext, useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import api from "../api/api";
import {
  buildGlobalSearchIndex,
  searchGlobal,
  hasAnyGlobalResults,
} from "../utils/globalSearch";
import { SEARCH_QUERY_KEYS } from "../hooks/useSearchInvalidation";

const GlobalSearchContext = createContext(null);

/**
 * GlobalSearchProvider
 * Aggregates data from existing React Query hooks and provides search functionality
 */
export function GlobalSearchProvider({ children }) {
  const { user } = useAuth();

  // Search UI state
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMouseHoverEnabled, setIsMouseHoverEnabled] = useState(true);

  // Fetch projects - uses standardized query key from SEARCH_QUERY_KEYS
  const { data: projects = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.projects(),
    queryFn: async () => {
      const response = await api.get("/projects/");
      return response.data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Fetch contacts - uses standardized query key from SEARCH_QUERY_KEYS
  const { data: contacts = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.contacts(user?.uid),
    queryFn: async () => {
      const response = await api.get("/contacts/");
      return response.data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Fetch todos/tasks - uses standardized query key from SEARCH_QUERY_KEYS
  const { data: tasks = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.todos(user?.uid),
    queryFn: async () => {
      const response = await api.get("/todos/");
      return response.data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Fetch receipts - uses standardized query key from SEARCH_QUERY_KEYS
  const { data: receipts = [] } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.receipts(user?.uid),
    queryFn: async () => {
      const response = await api.get("/receipts");
      return response.data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Build search index when data changes
  const searchIndex = useMemo(() => {
    return buildGlobalSearchIndex({
      projects,
      contacts,
      tasks,
      receipts,
    });
  }, [projects, contacts, tasks, receipts]);

  // Compute search results when query changes
  const results = useMemo(() => {
    if (!query.trim()) return null;
    return searchGlobal(query, searchIndex);
  }, [query, searchIndex]);

  // Check if there are any results
  const hasResults = useMemo(() => {
    return hasAnyGlobalResults(results);
  }, [results]);

  // Open search modal
  const openSearch = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Close search modal and reset query (used when clicking outside or selecting a result)
  const closeSearch = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setSelectedIndex(0);
  }, []);

  // Dismiss dropdown only (keep query intact) - used for Escape key
  // Also disables mouse hover to prevent accidental selection changes when reopening
  const dismissDropdown = useCallback(() => {
    setIsOpen(false);
    setIsMouseHoverEnabled(false);
  }, []);

  // Update search query (also opens search if closed)
  const updateQuery = useCallback((newQuery) => {
    setQuery(newQuery);
    // If user starts typing and dropdown is closed, reopen it
    if (newQuery.trim() && !isOpen) {
      setIsOpen(true);
    }
  }, [isOpen]);

  const value = {
    // UI state
    isOpen,
    setIsOpen,
    openSearch,
    closeSearch,
    dismissDropdown,
    
    // Search state
    query,
    setQuery: updateQuery,
    results,
    hasResults,
    
    // Selection state (persists across dropdown open/close)
    selectedIndex,
    setSelectedIndex,
    
    // Mouse hover control (disabled after dismiss, re-enabled on intentional mouse move)
    isMouseHoverEnabled,
    setIsMouseHoverEnabled,
    
    // Raw data (for potential direct access)
    searchIndex,
  };

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
    </GlobalSearchContext.Provider>
  );
}

/**
 * Hook to use global search context
 */
export function useGlobalSearch() {
  const context = useContext(GlobalSearchContext);
  if (!context) {
    throw new Error("useGlobalSearch must be used within a GlobalSearchProvider");
  }
  return context;
}

export default GlobalSearchContext;
